import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  MenuItem,
  Typography,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  IconButton,
  Card,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, InfoOutlined, Inventory } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { AlertMessages } from '../AlertMessages';
import { createStockEntry, getStockEntries, getAllProducts } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { FormContainer } from './styles';

const BRANCHES = {
  ALTO_BRANCO: 'ALTO_BRANCO',
  CONCEICAO: 'CONCEICAO',
};

const DEFAULT_BRANCH = BRANCHES.ALTO_BRANCO;
const STORAGE_KEY = 'canteen_filial';

// Funções auxiliares
const isValidBranch = (branch) => {
  return branch === BRANCHES.ALTO_BRANCO || branch === BRANCHES.CONCEICAO;
};

const getBranchFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isValidBranch(saved) ? saved : null;
  } catch (error) {
    console.error('[StockForm] Erro ao ler localStorage:', error);
    return null;
  }
};

const saveBranchToStorage = (branch) => {
  try {
    if (isValidBranch(branch)) {
      localStorage.setItem(STORAGE_KEY, branch);
      return true;
    }
  } catch (error) {
    console.error('[StockForm] Erro ao salvar filial no localStorage:', error);
  }
  return false;
};

const getValidBranch = (branch) => {
  if (isValidBranch(branch)) {
    return branch;
  }
  const savedBranch = getBranchFromStorage();
  return savedBranch || DEFAULT_BRANCH;
};

export const StockForm = () => {
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [entryDate, setEntryDate] = useState(dayjs());
  const [branch, setBranch] = useState(() => {
    const savedBranch = getBranchFromStorage();
    const validBranch = savedBranch || DEFAULT_BRANCH;
    saveBranchToStorage(validBranch);
    return validBranch;
  });
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filterTab, setFilterTab] = useState(0);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  // Sincronizar estado com localStorage quando branch mudar
  useEffect(() => {
    if (isValidBranch(branch)) {
      saveBranchToStorage(branch);
    } else {
      // Se branch ficou inválido, corrigir
      const validBranch = getValidBranch(branch);
      setBranch(validBranch);
    }
  }, [branch]);

  // Recarregar dados quando branch mudar
  useEffect(() => {
    loadProducts();
    loadEntries();
  }, [branch]);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const data = await getAllProducts();
      const filtered = (data.products || []).filter(
        (p) => !p.branch || p.branch === branch
      );
      setProducts(filtered);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setProductsLoading(false);
    }
  };

  const loadEntries = async () => {
    try {
      setEntriesLoading(true);
      const currentBranch = getValidBranch(branch);

      if (!isValidBranch(currentBranch)) {
        setAlertState({
          show: true,
          type: 'error',
          message: 'Selecione uma filial válida para visualizar as movimentações',
        });
        setEntries([]);
        return;
      }

      const data = await getStockEntries({ branch: currentBranch });
      setEntries(data.entries || []);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!productId || !quantity || !entryDate) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preencha produto, quantidade e data',
      });
      return;
    }

    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Quantidade deve ser maior que zero',
      });
      return;
    }

    const finalBranch = getValidBranch(branch);

    if (!isValidBranch(finalBranch)) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Erro: Filial não pôde ser determinada. Por favor, selecione uma filial e tente novamente.',
      });
      return;
    }

    // Sincronizar estado se necessário
    if (branch !== finalBranch) {
      setBranch(finalBranch);
    }

    try {
      setLoading(true);

      const entryData = {
        productId: parseInt(productId, 10),
        quantity: qty,
        entryDate: entryDate.format('YYYY-MM-DD'),
        branch: finalBranch,
      };

      await createStockEntry(entryData);

      setAlertState({
        show: true,
        type: 'success',
        message: 'Entrada de estoque registrada com sucesso!',
      });

      // Limpar formulário
      setProductId('');
      setQuantity('');
      setEntryDate(dayjs());

      // Recarregar lista
      await loadEntries();
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (newBranch) => {
    if (isValidBranch(newBranch)) {
      setBranch(newBranch);
      saveBranchToStorage(newBranch);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Filtrar entradas por tipo (entrada/saída) - já filtradas por branch no backend
  const filteredEntries = entries.filter((entry) => {
    const qty = parseInt(entry.quantity, 10);
    if (filterTab === 1) return qty > 0;
    if (filterTab === 2) return qty < 0;
    return true;
  });

  // Calcular estatísticas apenas com entradas da filial correta
  const entriesForBranch = entries.filter(
    (e) => e.branch === branch
  );

  const stats = {
    totalEntries: entriesForBranch
      .filter((e) => parseInt(e.quantity, 10) > 0)
      .reduce((sum, e) => sum + Math.abs(parseInt(e.quantity, 10)), 0),
    totalExits: entriesForBranch
      .filter((e) => parseInt(e.quantity, 10) < 0)
      .reduce((sum, e) => sum + Math.abs(parseInt(e.quantity, 10)), 0),
    currentStock: entriesForBranch.reduce(
      (sum, e) => sum + parseInt(e.quantity, 10),
      0
    ),
  };

  const handleFilterChange = (event, newValue) => {
    setFilterTab(newValue);
  };

  const selectValue = getValidBranch(branch);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormContainer>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: '#0b4f6c' }}
              >
                Cadastrar Entrada de Estoque
              </Typography>

              <TextField
                select
                label="Produto"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                fullWidth
                required
                variant="outlined"
                disabled={productsLoading || loading}
              >
                {productsLoading ? (
                  <MenuItem disabled>Carregando produtos...</MenuItem>
                ) : products.length === 0 ? (
                  <MenuItem disabled>Nenhum produto cadastrado</MenuItem>
                ) : (
                  products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                    </MenuItem>
                  ))
                )}
              </TextField>

              <TextField
                label="Quantidade"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                fullWidth
                required
                variant="outlined"
                inputProps={{ min: 1 }}
                helperText="Quantidade de produtos a dar entrada"
                disabled={loading}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                  label="Data da Entrada"
                  value={entryDate}
                  onChange={(newValue) => setEntryDate(newValue)}
                  format="DD/MM/YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      variant="outlined"
                      disabled={loading}
                    />
                  )}
                />
              </LocalizationProvider>

              <FormControl fullWidth required>
                <InputLabel id="branch-select-label">Filial *</InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  value={selectValue}
                  label="Filial *"
                  onChange={(e) => handleBranchChange(e.target.value)}
                  disabled={loading}
                  error={!isValidBranch(branch)}
                  required
                >
                  <MenuItem value={BRANCHES.ALTO_BRANCO}>Alto Branco</MenuItem>
                  <MenuItem value={BRANCHES.CONCEICAO}>Conceição</MenuItem>
                </Select>
              </FormControl>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || productsLoading}
                sx={{
                  bgcolor: '#1e958c',
                  padding: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0b4f6c' },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Registrar Entrada'
                )}
              </Button>
            </Box>
          </FormContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormContainer>
            <Box>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: '#0b4f6c' }}
                  >
                    Movimentações de Estoque
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="branch-filter-label">Filial</InputLabel>
                    <Select
                      labelId="branch-filter-label"
                      id="branch-filter"
                      value={branch}
                      label="Filial"
                      onChange={(e) => handleBranchChange(e.target.value)}
                    >
                      <MenuItem value={BRANCHES.ALTO_BRANCO}>Alto Branco</MenuItem>
                      <MenuItem value={BRANCHES.CONCEICAO}>Conceição</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Card
                      sx={{
                        bgcolor: '#e8f5e9',
                        border: '1px solid #4caf50',
                        textAlign: 'center',
                        py: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <ArrowUpward sx={{ color: '#4caf50', fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 500 }}
                        >
                          Entradas
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: '#2e7d32', fontWeight: 700 }}
                        >
                          {stats.totalEntries}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card
                      sx={{
                        bgcolor: '#ffebee',
                        border: '1px solid #f44336',
                        textAlign: 'center',
                        py: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <ArrowDownward sx={{ color: '#f44336', fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 500 }}
                        >
                          Saídas
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ color: '#c62828', fontWeight: 700 }}
                        >
                          {stats.totalExits}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card
                      sx={{
                        bgcolor: '#e3f2fd',
                        border: '1px solid #2196f3',
                        textAlign: 'center',
                        py: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Inventory sx={{ color: '#2196f3', fontSize: 20 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontWeight: 500 }}
                        >
                          Saldo
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: stats.currentStock > 0 ? '#1976d2' : '#666',
                            fontWeight: 700,
                          }}
                        >
                          {stats.currentStock}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={filterTab}
                    onChange={handleFilterChange}
                    variant="fullWidth"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: '600',
                        minHeight: '36px',
                        color: '#666',
                        '&.Mui-selected': {
                          color: '#1e958c',
                        },
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#1e958c',
                        height: 3,
                      },
                    }}
                  >
                    <Tab label="Todas" />
                    <Tab
                      label="Entradas"
                      icon={<ArrowUpward sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                    />
                    <Tab
                      label="Saídas"
                      icon={<ArrowDownward sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                    />
                  </Tabs>
                </Box>
              </Box>

              {entriesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredEntries.length === 0 ? (
                <Box sx={{ textAlign: 'center', padding: 4, color: '#666' }}>
                  {filterTab === 1 && 'Nenhuma entrada registrada'}
                  {filterTab === 2 && 'Nenhuma saída registrada'}
                  {filterTab === 0 && 'Nenhuma movimentação registrada'}
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    maxHeight: 500,
                    '& .MuiTable-root': {
                      tableLayout: 'fixed',
                      width: '100%',
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{
                            width: '40%',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          Produto
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '20%',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          Qtd
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '25%',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          Data
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            width: '15%',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            pr: 2,
                          }}
                        >
                          Usuário
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEntries.slice(0, 20).map((entry) => {
                        const qty = parseInt(entry.quantity, 10);
                        const isEntry = qty > 0;

                        return (
                          <TableRow
                            key={entry.id}
                            sx={{
                              bgcolor: isEntry
                                ? 'rgba(76, 175, 80, 0.03)'
                                : 'rgba(244, 67, 54, 0.03)',
                              '&:hover': {
                                bgcolor: isEntry
                                  ? 'rgba(76, 175, 80, 0.08)'
                                  : 'rgba(244, 67, 54, 0.08)',
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {isEntry ? (
                                  <ArrowUpward
                                    sx={{ color: '#4caf50', fontSize: 14, flexShrink: 0 }}
                                  />
                                ) : (
                                  <ArrowDownward
                                    sx={{ color: '#f44336', fontSize: 14, flexShrink: 0 }}
                                  />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {entry.product?.name || `#${entry.productId}`}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={isEntry ? `+${Math.abs(qty)}` : `-${Math.abs(qty)}`}
                                color={isEntry ? 'success' : 'error'}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: '24px',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                              {formatDate(entry.entryDate)}
                            </TableCell>
                            <TableCell align="center" sx={{ pr: 2 }}>
                              {entry.creator?.name ? (
                                <Tooltip title={entry.creator.name} arrow>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: '#1e958c',
                                      padding: '4px',
                                    }}
                                  >
                                    <InfoOutlined sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </FormContainer>
        </Grid>
      </Grid>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
